import React from 'react';
import { Vector3 } from 'three';

import { isValidNumber } from '../../../../../utils/math/number';
import { CoordinateManager } from '../../../../../viewer/coordinate-manager';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { InputHandler } from '../../../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { VolumeCalculatorTool } from '../../../../../viewer/tools/volume-calculator-tool';
import { Coordinates } from '../../../../../viewer/utilities/math-helpers';
import { useNonce } from '../../../../hooks/use-nonce';
import { useSubscribe } from '../../../../hooks/use-subscribe';

type ItemValue = {
  isValid: boolean;
  value: string;
  prevValidValue: string;
};
export type Item = {
  n: ItemValue;
  e: ItemValue;
  mas: ItemValue;
};

const useAssetPoints = (asset: Geometry) => {
  const [_, refresh] = useNonce();
  const [originalItems, setOriginalItems] = React.useState<Map<string, Item>>(new Map());
  const [currentItems, setCurrentItems] = React.useState<Map<string, Item>>(new Map());
  const [orderByItemIds, setOrderByItemIds] = React.useState<string[]>([]);

  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, reInit);

  // Refresh when hovering a point.
  useSubscribe(SubscriptionTopic.AssetHighlightedIndexChanged, refresh);

  // Handle asset changed (new, deleted, changed points).
  useSubscribe(SubscriptionTopic.AssetChanged, (changed) => {
    if (changed.uuid !== asset.uuid) {
      return;
    }

    const localPoints = asset.getLocalPoints();
    const assetPointIds = localPoints.map((x) => x.id);
    const originalPointIds = [...originalItems.keys()];

    const newOriginalItems = new Map(originalItems);
    const newCurrentItems = new Map(currentItems);

    // Points removed?
    const pointsRemoved = originalPointIds.filter((x) => !assetPointIds.includes(x));
    for (const id of pointsRemoved) {
      newOriginalItems.delete(id);
      newCurrentItems.delete(id);
    }

    // Points added?
    const pointsAdded = assetPointIds.filter((x) => !originalPointIds.includes(x));
    for (const id of pointsAdded) {
      const pos = localPoints.find((x) => x.id === id)!;

      newOriginalItems.set(id, buildItem(pos.point));
      newCurrentItems.set(id, buildItem(pos.point));
    }

    // Update position of all points.
    for (const point of localPoints) {
      const item = buildItem(point.point);
      const currentItem = newCurrentItems.get(point.id)!;

      if (keepCurrentValue(currentItem, item, 'n')) {
        item.n = currentItem.n;
      }
      if (keepCurrentValue(currentItem, item, 'e')) {
        item.e = currentItem.e;
      }
      if (keepCurrentValue(currentItem, item, 'mas')) {
        item.mas = currentItem.mas;
      }

      newCurrentItems.set(point.id, item);
    }

    setOriginalItems(newOriginalItems);
    setCurrentItems(newCurrentItems);
    setOrderByItemIds(localPoints.map((x) => x.id));
  });

  /**
   * Checks whether, given a coord and a new coord, the given coord should be kept
   */
  const keepCurrentValue = (coord: Item, newCoord: Item, coordKey: keyof Item) => {
    // If current value is not valid and previous valid value equals new value, then keep
    // current value.
    if (
      !coord[coordKey].isValid &&
      parseFloat(newCoord[coordKey].value) === parseFloat(coord[coordKey].prevValidValue)
    ) {
      return true;
    }

    // Otherwise, keep if current value equals new value.
    return parseFloat(newCoord[coordKey].value) === parseFloat(coord[coordKey].value);
  };

  function init() {
    const items = new Map(
      asset.getLocalPoints().map((localPoint) => [localPoint.id, buildItem(localPoint.point)]),
    );

    setOriginalItems(items);
    setCurrentItems(items);
    setOrderByItemIds(asset.getLocalPoints().map((item) => item.id));
  }

  function reInit() {
    const localPoints = asset.getLocalPoints();
    const newOriginalItems = new Map(
      orderByItemIds.map((id, index) => [localPoints[index].id, originalItems.get(id)!]),
    );

    const newCurrentItems = new Map(
      localPoints.map((localPoint) => [localPoint.id, buildItem(localPoint.point)]),
    );

    setOriginalItems(newOriginalItems);
    setCurrentItems(newCurrentItems);
    setOrderByItemIds(asset.getLocalPoints().map((item) => item.id));
  }

  /**
   * Returns an object representing the specified position in the form.
   */
  const buildItem = React.useCallback(
    (pos: Vector3): Item => {
      const coord = CoordinateManager.getInstance().localToProjectCoords(
        pos.clone().add(asset.position),
      );

      return {
        n: {
          isValid: true,
          value: coord.northing.toFixed(2),
          prevValidValue: coord.northing.toFixed(2),
        },
        e: {
          isValid: true,
          value: coord.easting.toFixed(2),
          prevValidValue: coord.easting.toFixed(2),
        },
        mas: {
          isValid: true,
          value: coord.height.toFixed(2),
          prevValidValue: coord.height.toFixed(2),
        },
      };
    },
    [asset],
  );

  React.useEffect(init, [asset, buildItem]);

  /**
   * Updates the item with a specified ID.
   */
  const updateItem = (
    id: string,
    value: string,
    cb: (point: Item, item: Pick<ItemValue, 'isValid' | 'value'>) => Partial<Item>,
  ) => {
    const newItems = new Map(currentItems);
    const item = newItems.get(id)!;

    newItems.set(id, {
      ...item,
      ...cb(item, {
        isValid: isValidNumber(value),
        value,
      }),
    });

    setCurrentItems(newItems);
  };

  /**
   * Updates a specific point in the asset points collection.
   */
  const updateAssetPoint = async (
    id: string,
    value: string,
    part: 'setX' | 'setY' | 'setZ',
    cb: (current: Item) => Coordinates,
  ) => {
    if (!isValidNumber(value)) {
      return;
    }

    const prop = part === 'setX' ? 'x' : part === 'setY' ? 'y' : 'z';
    const localPoint = asset.getLocalPoints().find((x) => x.id === id)!;
    localPoint.point[part](
      CoordinateManager.getInstance()
        .projectCoordsToLocal(cb(currentItems.get(id)!))
        .sub(asset.position)[prop],
    );

    await refreshAsset();
  };

  const updateNorthing = async (id: string, value: string) => {
    updateItem(id, value, (point, item) => ({
      n: {
        ...point.n,
        ...item,
      },
    }));

    await updateAssetPoint(id, value, 'setY', (current: Item) => ({
      easting: Number(current.e.value),
      height: Number(current.mas.value),
      northing: parseFloat(value),
    }));
  };

  const updateEasting = async (id: string, value: string) => {
    updateItem(id, value, (point, item) => ({
      e: {
        ...point.e,
        ...item,
      },
    }));

    await updateAssetPoint(id, value, 'setX', (current: Item) => ({
      easting: parseFloat(value),
      height: Number(current.mas.value),
      northing: Number(current.n.value),
    }));
  };

  const updateMas = async (id: string, value: string) => {
    updateItem(id, value, (point, item) => ({
      mas: {
        ...point.mas,
        ...item,
      },
    }));

    await updateAssetPoint(id, value, 'setZ', (current: Item) => ({
      easting: Number(current.e.value),
      height: parseFloat(value),
      northing: Number(current.n.value),
    }));
  };

  const restoreItem = async (id: string) => {
    const newItems = new Map(currentItems);
    newItems.set(id, originalItems.get(id)!);
    setCurrentItems(newItems);

    restoreCoordinate(id);
    await refreshAsset();
  };

  const restoreCoordinate = (id: string) => {
    const originalItem = originalItems.get(id)!;
    const originalCoord = CoordinateManager.getInstance()
      .projectCoordsToLocal({
        northing: Number(originalItem.n.value),
        easting: Number(originalItem.e.value),
        height: Number(originalItem.mas.value),
      })
      .sub(asset.position);

    const localPoint = asset.getLocalPoints().find((x) => x.id === id)!;
    localPoint.point.setX(originalCoord.x);
    localPoint.point.setY(originalCoord.y);
    localPoint.point.setZ(originalCoord.z);
  };

  const restoreAllItems = async () => {
    setCurrentItems(new Map(originalItems));

    for (const id of originalItems.keys()) {
      restoreCoordinate(id);
    }

    await refreshAsset();
  };

  const flattenGeometry = async (id: string) => {
    const item = currentItems.get(id)!;
    const localPoint = asset.getLocalPoints().find((x) => x.id === id)!;

    const newItems = new Map(currentItems);
    for (const id of currentItems.keys()) {
      newItems.set(id, {
        ...newItems.get(id)!,
        mas: item.mas,
      });

      asset
        .getLocalPoints()
        .find((x) => x.id === id)!
        .point.setZ(localPoint.point.z);
    }

    setCurrentItems(newItems);
    await refreshAsset();
  };

  async function refreshAsset() {
    asset.onTransformed();
    await asset.onChange();
  }

  return {
    currentItems,
    flattenGeometry,
    orderByItemIds,
    originalItems,
    restoreAllItems,
    restoreItem,
    updateEasting,
    updateMas,
    updateNorthing,
  };
};

/**
 * Whether the points of an asset can be listed and edited: it is visible, it has no volume
 * calculation, and no volume tool is using it, since its points must stay as they are while it
 * has a volume.
 */
const canEditPoints = (asset: Geometry) =>
  asset.getStateVisibility() &&
  asset.editable &&
  !isVolumeToolActive() &&
  !isVolumeInspectorToolActive();

function isVolumeToolActive() {
  const activeTool = InputHandler.instance.currentTool();
  return (
    InputHandler.instance.isVolumeToolActive() &&
    (activeTool as VolumeCalculatorTool).hasAssetSelected
  );
}

function isVolumeInspectorToolActive() {
  return InputHandler.instance.isVolumeInspectorToolActive();
}

export { canEditPoints, useAssetPoints };
